import React, { useState, useEffect } from 'react';
import { Archive, X, Check, Clipboard, Hand, Plus, Edit2, Trash2, Camera, CornerUpLeft } from 'lucide-react';
import inventoryService from '../services/inventoryService';
import '../styles/inventory.css';

const InventoryManagement = () => {
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [borrows, setBorrows] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtreler
  const [filters, setFilters] = useState({
    workshop: '',
    category: '',
    status: '',
    search: ''
  });

  // Modal durumları
  const [showItemModal, setShowItemModal] = useState(false);
  const [showBorrowModal, setShowBorrowModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showMonthlyCheckModal, setShowMonthlyCheckModal] = useState(false);
  const [photoModal, setPhotoModal] = useState({ open: false, title: '', photos: [] });
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedBorrow, setSelectedBorrow] = useState(null);

  // Form verileri
  const [itemForm, setItemForm] = useState({
    name: '',
    category: '',
    workshop: '',
    quantity: 1,
    unit: 'Adet',
    condition: 'İyi',
    status: 'Mevcut',
    location: '',
    description: '',
    serialNumber: '',
    purchaseDate: '',
    purchasePrice: 0,
    supplier: '',
    imageUrl: ''
  });

  const [borrowForm, setBorrowForm] = useState({
    itemId: '',
    itemName: '',
    borrowerName: '',
    borrowerEmail: '',
    borrowerPhone: '',
    department: '',
    quantity: 1,
    expectedReturnDate: '',
    borrowCondition: 'İyi',
    borrowPhotos: [],
    notes: ''
  });

  const [returnForm, setReturnForm] = useState({
    returnPhotos: [],
    returnCondition: 'İyi',
    returnNotes: ''
  });

  // Sabitler
  const WORKSHOPS = [
    "Necmettin Erbakan Teknoloji Atölyesi",
    "Aksaray Hanımlar Binası",
    "Diğer"
  ];

  const CATEGORIES = [
    "Elektronik",
    "El Aletleri",
    "Bilgisayar ve Yazılım",
    "Malzeme",
    "Mobilya",
    "Diğer"
  ];

  const CONDITIONS = ["Yeni", "İyi", "Orta", "Bakım Gerekli", "Arızalı"];
  const STATUSES = ["Mevcut", "Ödünç Verildi", "Bakımda", "Kayıp"];

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'items') {
        const response = await inventoryService.getAllItems(filters);
        setItems(response.items || []);
      } else if (activeTab === 'borrows') {
        const response = await inventoryService.getAllBorrows();
        setBorrows(response.borrows || []);
      } else if (activeTab === 'statistics') {
        const stats = await inventoryService.getStatistics(filters.workshop);
        setStatistics(stats);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============ Öğe İşlemleri ============

  const handleCreateItem = async () => {
    setError('');
    setSuccess('');

    if (itemForm.purchaseDate && !/^\d{2}-\d{2}-\d{4}$/.test(itemForm.purchaseDate)) {
      setError('Alış tarihi geçerli bir formatta olmalı. Format: DD-MM-YYYY');
      return;
    }
    
    try {
      await inventoryService.createItem(itemForm);
      setSuccess('Öğe başarıyla oluşturuldu');
      setShowItemModal(false);
      resetItemForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateItem = async () => {
    setError('');
    setSuccess('');
    
    try {
      await inventoryService.updateItem(selectedItem._id, itemForm);
      setSuccess('Öğe başarıyla güncellendi');
      setShowItemModal(false);
      setSelectedItem(null);
      resetItemForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await inventoryService.deleteItem(itemId);
      setSuccess('Öğe başarıyla silindi');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openItemModal = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setItemForm(item);
    } else {
      setSelectedItem(null);
      resetItemForm();
    }
    setShowItemModal(true);
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      category: '',
      workshop: '',
      quantity: 1,
      unit: 'Adet',
      condition: 'İyi',
      status: 'Mevcut',
      location: '',
      description: '',
      serialNumber: '',
      purchaseDate: '',
      purchasePrice: 0,
      supplier: '',
      imageUrl: ''
    });
  };

  // ============ Ödünç İşlemleri ============

  const handleCreateBorrow = async () => {
    setError('');
    setSuccess('');

    if (!/^\d{2}-\d{2}-\d{4}$/.test(borrowForm.expectedReturnDate)) {
      setError('Beklenen iade tarihi geçerli bir formatta olmalı. Format: DD-MM-YYYY');
      return;
    }
    
    try {
      await inventoryService.createBorrow(borrowForm);
      setSuccess('Ödünç kaydı oluşturuldu');
      setShowBorrowModal(false);
      resetBorrowForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReturnItem = async () => {
    setError('');
    setSuccess('');
    
    try {
      await inventoryService.returnItem(selectedBorrow._id, returnForm);
      setSuccess('İade işlemi tamamlandı');
      setShowReturnModal(false);
      setSelectedBorrow(null);
      resetReturnForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openBorrowModal = (item) => {
    setBorrowForm({
      ...borrowForm,
      itemId: item._id,
      itemName: item.name
    });
    setShowBorrowModal(true);
  };

  const openReturnModal = (borrow) => {
    setSelectedBorrow(borrow);
    setShowReturnModal(true);
  };

  const resetBorrowForm = () => {
    setBorrowForm({
      itemId: '',
      itemName: '',
      borrowerName: '',
      borrowerEmail: '',
      borrowerPhone: '',
      department: '',
      quantity: 1,
      expectedReturnDate: '',
      borrowCondition: 'İyi',
      borrowPhotos: [],
      notes: ''
    });
  };

  const resetReturnForm = () => {
    setReturnForm({
      returnPhotos: [],
      returnCondition: 'İyi',
      returnNotes: ''
    });
  };

  // ============ Fotoğraf Yükleme ============

  const handlePhotoUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const response = await inventoryService.uploadPhoto(file);
      
      if (type === 'item') {
        setItemForm({ ...itemForm, imageUrl: response.url });
      } else if (type === 'borrow') {
        setBorrowForm({
          ...borrowForm,
          borrowPhotos: [...borrowForm.borrowPhotos, response.url]
        });
      } else if (type === 'return') {
        setReturnForm({
          ...returnForm,
          returnPhotos: [...returnForm.returnPhotos, response.url]
        });
      }
      
      setSuccess('Fotoğraf yüklendi');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removePhoto = (url, type) => {
    if (type === 'borrow') {
      setBorrowForm({
        ...borrowForm,
        borrowPhotos: borrowForm.borrowPhotos.filter(p => p !== url)
      });
    } else if (type === 'return') {
      setReturnForm({
        ...returnForm,
        returnPhotos: returnForm.returnPhotos.filter(p => p !== url)
      });
    }
  };

  const openPhotoModal = (photos, title = 'Fotoğraflar') => {
    if (!photos || photos.length === 0) return;
    setPhotoModal({ open: true, title, photos });
  };

  const closePhotoModal = () => setPhotoModal({ open: false, title: '', photos: [] });

  // ============ Render ============

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1><Archive className="inline-icon"/> Envanter Yönetimi</h1>
        <p>Atölye malzemelerini takip edin ve yönetin</p>
      </div>

      {/* Bildirimler */}
      {error && (
        <div className="notification error">
          <X className="inline-icon"/> {error}
          <button onClick={() => setError('')}><X /></button>
        </div>
      )}
      
      {success && (
        <div className="notification success">
          <Check className="inline-icon"/> {success}
          <button onClick={() => setSuccess('')}><X /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="inventory-tabs">
        <button
          className={activeTab === 'items' ? 'active' : ''}
          onClick={() => setActiveTab('items')}
        >
          <Clipboard className="inline-icon"/> Envanter Listesi
        </button>
        <button
          className={activeTab === 'borrows' ? 'active' : ''}
          onClick={() => setActiveTab('borrows')}
        >
          <Hand className="inline-icon"/> Ödünç İşlemleri
        </button>
        <button
          className={activeTab === 'statistics' ? 'active' : ''}
          onClick={() => setActiveTab('statistics')}
        >
          <Clipboard className="inline-icon"/> İstatistikler
        </button>
      </div>

      {/* İçerik */}
      <div className="inventory-content">
        {activeTab === 'items' && (
          <div className="items-section">
            {/* Filtreler */}
            <div className="filters">
              <select
                value={filters.workshop}
                onChange={(e) => setFilters({ ...filters, workshop: e.target.value })}
              >
                <option value="">Tüm Atölyeler</option>
                {WORKSHOPS.map(w => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>

              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="">Tüm Kategoriler</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Tüm Durumlar</option>
                {STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Ara..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />

              <button className="btn-primary" onClick={() => openItemModal()}>
                <Plus className="inline-icon"/> Yeni Öğe
              </button>
            </div>

            {/* Öğeler Tablosu */}
            {loading ? (
              <div className="loading">Yükleniyor...</div>
            ) : (
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Fotoğraf</th>
                    <th>İsim</th>
                    <th>Kategori</th>
                    <th>Atölye</th>
                    <th>Miktar</th>
                    <th>Durum</th>
                    <th>Konum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item._id}>
                      <td>
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name} className="item-thumbnail" />
                        ) : (
                          <div className="no-image"><Archive className="inline-icon"/></div>
                        )}
                      </td>
                      <td>{item.name}</td>
                      <td>{item.category}</td>
                      <td>{item.workshop}</td>
                      <td>{item.quantity} {item.unit}</td>
                      <td>
                        <span className={`status-badge ${item.status.toLowerCase().replace(' ', '-')}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>{item.location || '-'}</td>
                      <td className="actions">
                        <button onClick={() => openItemModal(item)} title="Düzenle">
                          <Edit2 />
                        </button>
                        <button onClick={() => openBorrowModal(item)} title="Ödünç Ver">
                          <Hand />
                        </button>
                        <button onClick={() => handleDeleteItem(item._id)} title="Sil">
                          <Trash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'borrows' && (
          <div className="borrows-section">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Öğe</th>
                  <th>Ödünç Alan</th>
                  <th>E-posta</th>
                  <th>Miktar</th>
                  <th>Ödünç Tarihi</th>
                  <th>Beklenen İade</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {borrows.map(borrow => (
                  <tr key={borrow._id}>
                    <td>{borrow.itemName}</td>
                    <td>{borrow.borrowerName}</td>
                    <td>{borrow.borrowerEmail}</td>
                    <td>{borrow.quantity}</td>
                    <td>{new Date(borrow.borrowDate).toLocaleDateString('tr-TR')}</td>
                    <td>{new Date(borrow.expectedReturnDate).toLocaleDateString('tr-TR')}</td>
                    <td>
                      <span className={`status-badge ${borrow.status}`}>
                        {borrow.status === 'active' ? 'Aktif' : 'İade Edildi'}
                      </span>
                    </td>
                    <td className="actions">
                      {borrow.status === 'active' && (
                        <button onClick={() => openReturnModal(borrow)} title="İade Et">
                          <CornerUpLeft className="inline-icon"/> İade
                        </button>
                      )}
                      {borrow.borrowPhotos && borrow.borrowPhotos.length > 0 && (
                        <button
                          title="Fotoğraflar"
                          onClick={() => openPhotoModal(borrow.borrowPhotos, `Fotoğraflar - ${borrow.itemName}`)}
                        >
                          <Camera />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'statistics' && statistics && (
          <div className="statistics-section">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Toplam Öğe</h3>
                <div className="stat-value">{statistics.totalItems}</div>
              </div>
              <div className="stat-card">
                <h3>Aktif Ödünçler</h3>
                <div className="stat-value">{statistics.activeBorrows}</div>
              </div>
              <div className="stat-card">
                <h3>Gecikmiş İadeler</h3>
                <div className="stat-value">{statistics.overdueBorrows}</div>
              </div>
            </div>

            <div className="charts">
              <div className="chart-card">
                <h3>Kategoriye Göre Dağılım</h3>
                <div className="category-list">
                  {statistics.categoryDistribution?.map(cat => (
                    <div key={cat._id} className="category-item">
                      <span>{cat._id}</span>
                      <span className="category-count">{cat.count} öğe</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>Durum Dağılımı</h3>
                <div className="status-list">
                  {statistics.statusDistribution?.map(status => (
                    <div key={status._id} className="status-item">
                      <span>{status._id}</span>
                      <span className="status-count">{status.count} öğe</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Öğe Modal */}
      {showItemModal && (
        <div className="modal-overlay" onClick={() => setShowItemModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedItem ? 'Öğeyi Düzenle' : 'Yeni Öğe Ekle'}</h2>
              <button onClick={() => setShowItemModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>İsim *</label>
                  <input
                    type="text"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Kategori *</label>
                  <select
                    value={itemForm.category}
                    onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                    required
                  >
                    <option value="">Seçin</option>
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Atölye *</label>
                  <select
                    value={itemForm.workshop}
                    onChange={(e) => setItemForm({ ...itemForm, workshop: e.target.value })}
                    required
                  >
                    <option value="">Seçin</option>
                    {WORKSHOPS.map(w => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Miktar *</label>
                  <input
                    type="number"
                    value={itemForm.quantity}
                    onChange={(e) => setItemForm({ ...itemForm, quantity: parseInt(e.target.value) })}
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Birim</label>
                  <input
                    type="text"
                    value={itemForm.unit}
                    onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Durum</label>
                  <select
                    value={itemForm.condition}
                    onChange={(e) => setItemForm({ ...itemForm, condition: e.target.value })}
                  >
                    {CONDITIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Konum</label>
                  <input
                    type="text"
                    value={itemForm.location}
                    onChange={(e) => setItemForm({ ...itemForm, location: e.target.value })}
                    placeholder="Örn: Raf 3, Çekmece 2"
                  />
                </div>

                <div className="form-group">
                  <label>Seri No</label>
                  <input
                    type="text"
                    value={itemForm.serialNumber}
                    onChange={(e) => setItemForm({ ...itemForm, serialNumber: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Alış Tarihi</label>
                  <input
                    type="date"
                    value={itemForm.purchaseDate}
                    onChange={(e) => setItemForm({ ...itemForm, purchaseDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Alış Fiyatı (TL)</label>
                  <input
                    type="number"
                    value={itemForm.purchasePrice}
                    onChange={(e) => setItemForm({ ...itemForm, purchasePrice: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Tedarikçi</label>
                  <input
                    type="text"
                    value={itemForm.supplier}
                    onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Açıklama</label>
                  <textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Fotoğraf</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e, 'item')}
                  />
                  {itemForm.imageUrl && (
                    <img src={itemForm.imageUrl} alt="Preview" className="image-preview" />
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowItemModal(false)}>
                İptal
              </button>
              <button
                className="btn-primary"
                onClick={selectedItem ? handleUpdateItem : handleCreateItem}
              >
                {selectedItem ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödünç Modal */}
      {showBorrowModal && (
        <div className="modal-overlay" onClick={() => setShowBorrowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ödünç Ver: {borrowForm.itemName}</h2>
              <button onClick={() => setShowBorrowModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Ödünç Alan *</label>
                  <input
                    type="text"
                    value={borrowForm.borrowerName}
                    onChange={(e) => setBorrowForm({ ...borrowForm, borrowerName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>E-posta *</label>
                  <input
                    type="email"
                    value={borrowForm.borrowerEmail}
                    onChange={(e) => setBorrowForm({ ...borrowForm, borrowerEmail: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={borrowForm.borrowerPhone}
                    onChange={(e) => setBorrowForm({ ...borrowForm, borrowerPhone: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Birim</label>
                  <input
                    type="text"
                    value={borrowForm.department}
                    onChange={(e) => setBorrowForm({ ...borrowForm, department: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Miktar *</label>
                  <input
                    type="number"
                    value={borrowForm.quantity}
                    onChange={(e) => setBorrowForm({ ...borrowForm, quantity: parseInt(e.target.value) })}
                    min="1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Beklenen İade Tarihi *</label>
                  <input
                    type="date"
                    value={borrowForm.expectedReturnDate}
                    onChange={(e) => setBorrowForm({ ...borrowForm, expectedReturnDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Ödünç Alırken Durum</label>
                  <select
                    value={borrowForm.borrowCondition}
                    onChange={(e) => setBorrowForm({ ...borrowForm, borrowCondition: e.target.value })}
                  >
                    {CONDITIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Not</label>
                  <textarea
                    value={borrowForm.notes}
                    onChange={(e) => setBorrowForm({ ...borrowForm, notes: e.target.value })}
                    rows="2"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Ödünç Alırken Fotoğraflar</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e, 'borrow')}
                    multiple
                  />
                  <div className="photo-grid">
                    {borrowForm.borrowPhotos.map((url, index) => (
                      <div key={index} className="photo-item">
                        <img src={url} alt={`Borrow ${index + 1}`} />
                        <button onClick={() => removePhoto(url, 'borrow')}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowBorrowModal(false)}>
                İptal
              </button>
              <button className="btn-primary" onClick={handleCreateBorrow}>
                Ödünç Ver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* İade Modal */}
      {showReturnModal && selectedBorrow && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>İade: {selectedBorrow.itemName}</h2>
              <button onClick={() => setShowReturnModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="borrow-info">
                <p><strong>Ödünç Alan:</strong> {selectedBorrow.borrowerName}</p>
                <p><strong>Miktar:</strong> {selectedBorrow.quantity}</p>
                <p><strong>Ödünç Tarihi:</strong> {new Date(selectedBorrow.borrowDate).toLocaleDateString('tr-TR')}</p>
                <p><strong>Ödünç Alırken Durum:</strong> {selectedBorrow.borrowCondition}</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>İade Durumu</label>
                  <select
                    value={returnForm.returnCondition}
                    onChange={(e) => setReturnForm({ ...returnForm, returnCondition: e.target.value })}
                  >
                    {CONDITIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>İade Notu</label>
                  <textarea
                    value={returnForm.returnNotes}
                    onChange={(e) => setReturnForm({ ...returnForm, returnNotes: e.target.value })}
                    rows="2"
                    placeholder="Varsa hasarlar veya notlar..."
                  />
                </div>

                <div className="form-group full-width">
                  <label>İade Fotoğrafları</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e, 'return')}
                    multiple
                  />
                  <div className="photo-grid">
                    {returnForm.returnPhotos.map((url, index) => (
                      <div key={index} className="photo-item">
                        <img src={url} alt={`Return ${index + 1}`} />
                        <button onClick={() => removePhoto(url, 'return')}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Ödünç alırken çekilen fotoğraflar */}
              {selectedBorrow.borrowPhotos && selectedBorrow.borrowPhotos.length > 0 && (
                <div className="borrow-photos-section">
                  <h4>Ödünç Alırken Çekilen Fotoğraflar</h4>
                  <div className="photo-grid">
                    {selectedBorrow.borrowPhotos.map((url, index) => (
                      <div key={index} className="photo-item">
                        <img src={url} alt={`Borrow ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowReturnModal(false)}>
                İptal
              </button>
              <button className="btn-primary" onClick={handleReturnItem}>
                İade Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {photoModal.open && (
        <div className="modal-overlay" onClick={closePhotoModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{photoModal.title}</h2>
              <button onClick={closePhotoModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="photo-grid">
                {photoModal.photos.map((url, index) => (
                  <div key={index} className="photo-item">
                    <img src={url} alt={`Photo ${index + 1}`} />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="open-new-tab">
                      Yeni sekmede aç
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
